import { useState } from 'react';
import QuickAddStrip from './QuickAddStrip.jsx';

/**
 * SCOPE-LEAD-ADD P3 (MF-16) — the phone add page behind `/add` (spec §3.5).
 *
 * The three owner rulings this file exists under:
 *  1. The NARROW option — ONE phone-usable add page, not whole-app responsiveness. So
 *     `/add` is reached by an early return in `App.jsx` that never mounts `.app-shell`
 *     (`styles.css:289-294` — `height: 100vh; overflow: hidden`) and never mounts the
 *     fixed-width sidebar (`:297-308`). Nothing here makes the board responsive.
 *  2. Entry is a browser bookmark of `/add` — no sidebar link, no QR, zero extra code.
 *  3. The phone opens NO lead card. Nothing on this page navigates, the strip's
 *     open-lead callback is deliberately withheld, and the proof-of-landing is the
 *     in-memory «added this session» list under the strip (name + status label).
 *
 * The add logic is NOT rebuilt: `QuickAddStrip` (P1) is the whole add surface — its
 * client-side routing, its debounce, its request-id guard, its verdict wordings and its
 * keyboard rules are the same bytes on all three surfaces. What this page owns is the
 * shell it deliberately lacks and the two thumb metrics of AC-4 — and it owns them under
 * its own class only (AC-5): no media query anywhere, no `.css` file touched.
 */

// Every selector below is prefixed `.mobile-add` (AC-5, pin MA-3), so this block cannot
// reach the board, `/bloggers` or any modal. The two `!important` declarations exist to
// beat the strip's own INLINE metric (`QuickAddStrip.jsx:256` — the 13 px toolbar row): a
// style attribute outranks a normal class rule, so only an author `!important` reaches the
// 16 px that stops iOS Safari zooming on focus. `min-height: 44px` is the AC-4 touch
// target as a measured floor, not as arithmetic (16 px + 24 px of padding + 2 px of border
// is only 42 px by the stated rule — the floor is what makes the number true). Pin MA-6
// pins BOTH sides of the override, so a future edit to the strip's inline metric turns a
// test red with the coupling named instead of drifting silently on the phone only.
const styles = `
  .mobile-add { padding: 16px; max-width: 520px; margin-inline: auto; }
  .mobile-add__title { margin: 0 0 14px; font-size: 18px; font-weight: 600; }
  .mobile-add .quick-add { width: 100%; }
  .mobile-add .form-input { padding: 12px 14px !important; font-size: 16px !important; min-height: 44px; }
  .mobile-add .board-action-btn { min-height: 44px; min-width: 44px; }
  .mobile-add__added-title { margin: 22px 0 6px; font-size: 13px; color: var(--text-muted); }
  .mobile-add__added { list-style: none; margin: 0; padding: 0; }
  .mobile-add__row { padding: 10px 0; border-top: 1px solid var(--neutral-40); font-size: 15px; }
  .mobile-add__status { color: var(--text-muted); }
`;

export default function MobileAddPage ({ onSubmit, disabled = false }) {

  const [added, setAdded] = useState([]);

  const handleSubmit = async (payload) => {
    const outcome = await onSubmit(payload);
    if (outcome?.ok) {
      setAdded((prev) => [
        {
          // the id is the only safe React key: two adds can derive the same name.
          id: outcome.lead.id,
          name: outcome.lead.name || payload.name,

          statusLabel: outcome.lead.statusLabel ?? null,
        },
        ...prev,
      ]);
    }
    return outcome;
  };

  return (
    <div className='mobile-add'>
      <style>{styles}</style>
      <h1 className='mobile-add__title'>Добавить блогера</h1>

      {/* No open-lead callback (ruling 3): rows 8 and 11 of the strip's state table lose
          their «Открыть» button and keep every word of their text. */}
      <QuickAddStrip onSubmit={handleSubmit} disabled={disabled} />

      {added.length > 0 && (
        <>
          <div className='mobile-add__added-title'>Добавлены в этой сессии</div>
          <ul className='mobile-add__added'>
            {added.map((row) => (
              <li className='mobile-add__row' key={row.id}>
                {row.name}
                {row.statusLabel ? <span className='mobile-add__status'>{` · ${row.statusLabel}`}</span> : null}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
